import type { NodeData, NodeId, NodeProps, Op, Rect, Transaction } from './types.ts';
import { IDENTITY, type Mat, localTransform, mul, transformRect, invert, apply as applyMat } from './geometry.ts';
import { makeNode, isContainer, isFrameLike } from './defaults.ts';
import { indexBetween } from './util.ts';

export interface ChangeEvent {
  tx: Transaction;
  /** Ids whose props changed (includes created). */
  changed: Set<NodeId>;
  created: Set<NodeId>;
  deleted: Set<NodeId>;
  /** True if parent/index/create/delete happened (tree structure changed). */
  structural: boolean;
  /** Changed property names across the transaction. */
  keys: Set<string>;
  /** Whether this was applied from a remote peer (not the local user). */
  remote: boolean;
}

export type ChangeListener = (e: ChangeEvent) => void;

export interface DocumentJSON {
  format: 'vellum';
  version: 1;
  nodes: NodeData[];
}

const GEOMETRY_KEYS = new Set(['x', 'y', 'width', 'height', 'rotation', 'flipX', 'flipY', 'parent']);

export class DocumentError extends Error {}

export class Document {
  readonly nodes = new Map<NodeId, NodeData>();
  readonly rootId: NodeId;
  private childCache = new Map<NodeId, NodeData[]>();
  private childIds = new Map<NodeId, Set<NodeId>>();
  private worldCache = new Map<NodeId, Mat>();
  /** Connector ids (layout reroutes them when what they attach to moves, without walking the file). */
  private connectorIds = new Set<NodeId>();
  private listeners = new Set<ChangeListener>();
  /** Monotonic counter, bumped on every applied transaction. */
  version = 0;

  constructor(rootId = '0:0') {
    this.rootId = rootId;
    const root = makeNode('DOCUMENT', { id: rootId, parent: null, name: 'Document' });
    this.insert(root);
  }

  static create(): Document {
    const doc = new Document();
    doc.apply({ ops: [{ op: 'create', node: makeNode('PAGE', { id: '0:1', parent: doc.rootId, index: 'V', name: 'Page 1' }) }] });
    return doc;
  }

  // -------------------------------------------------------------------------
  // Reads
  // -------------------------------------------------------------------------

  /** Every connector in the file. */
  *connectors(): Iterable<NodeData> {
    for (const id of this.connectorIds) {
      const n = this.nodes.get(id);
      if (n) yield n;
    }
  }

  get(id: NodeId | null | undefined): NodeData | undefined {
    return id == null ? undefined : this.nodes.get(id);
  }

  must(id: NodeId): NodeData {
    const n = this.nodes.get(id);
    if (!n) throw new DocumentError(`No node with id ${id}`);
    return n;
  }

  has(id: NodeId): boolean {
    return this.nodes.has(id);
  }

  /** Children sorted back-to-front. */
  children(id: NodeId): NodeData[] {
    let c = this.childCache.get(id);
    if (!c) {
      const ids = this.childIds.get(id);
      c = ids ? [...ids].map((i) => this.nodes.get(i)!).sort(cmpIndex) : [];
      this.childCache.set(id, c);
    }
    return c;
  }

  pages(): NodeData[] {
    return this.children(this.rootId).filter((n) => n.type === 'PAGE');
  }

  parentOf(id: NodeId): NodeData | undefined {
    return this.get(this.nodes.get(id)?.parent);
  }

  /** Ancestors from parent up to the document root. */
  ancestors(id: NodeId): NodeData[] {
    const out: NodeData[] = [];
    let p = this.parentOf(id);
    while (p) {
      out.push(p);
      p = this.parentOf(p.id);
    }
    return out;
  }

  isAncestor(ancestor: NodeId, id: NodeId): boolean {
    let p = this.nodes.get(id)?.parent;
    while (p) {
      if (p === ancestor) return true;
      p = this.nodes.get(p)?.parent ?? null;
    }
    return false;
  }

  pageOf(id: NodeId): NodeData | undefined {
    let n = this.nodes.get(id);
    while (n && n.type !== 'PAGE') n = this.get(n.parent);
    return n;
  }

  /** Depth-first walk (pre-order) of the subtree rooted at id. Return false from fn to skip children. */
  walk(id: NodeId, fn: (n: NodeData, depth: number) => boolean | void, depth = 0): void {
    const n = this.nodes.get(id);
    if (!n) return;
    if (fn(n, depth) === false) return;
    for (const c of this.children(id)) this.walk(c.id, fn, depth + 1);
  }

  subtree(id: NodeId): NodeData[] {
    const out: NodeData[] = [];
    this.walk(id, (n) => {
      out.push(n);
    });
    return out;
  }

  /** Matrix from node-local coordinates to page coordinates. */
  worldTransform(id: NodeId): Mat {
    const cached = this.worldCache.get(id);
    if (cached) return cached;
    const n = this.nodes.get(id);
    if (!n || n.type === 'PAGE' || n.type === 'DOCUMENT') return IDENTITY;
    const parent = n.parent ? this.worldTransform(n.parent) : IDENTITY;
    const m = mul(parent, localTransform(n));
    this.worldCache.set(id, m);
    return m;
  }

  /** Axis-aligned bounds of the node's box in page coordinates. */
  worldBounds(id: NodeId): Rect {
    const n = this.must(id);
    return transformRect(this.worldTransform(id), { x: 0, y: 0, width: n.width, height: n.height });
  }

  /** Convert a page-space point into the local space of a node. */
  toLocal(id: NodeId, p: { x: number; y: number }) {
    return applyMat(invert(this.worldTransform(id)), p);
  }

  /** Index key that places a new child at the given position (default: front-most). */
  indexAt(parentId: NodeId, position?: number): string {
    const kids = this.children(parentId);
    const pos = position === undefined || position > kids.length ? kids.length : Math.max(0, position);
    const before = pos > 0 ? kids[pos - 1].index : null;
    const after = pos < kids.length ? kids[pos].index : null;
    return indexBetween(before, after);
  }

  // -------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------

  /**
   * Apply a transaction atomically. Returns the inverse ops (apply them to undo).
   * Throws DocumentError (leaving the doc unchanged) if any op is invalid.
   */
  apply(tx: Transaction, opts: { remote?: boolean; silent?: boolean } = {}): Op[] {
    const inverse: Op[] = [];
    const ev: ChangeEvent = {
      tx,
      changed: new Set(),
      created: new Set(),
      deleted: new Set(),
      structural: false,
      keys: new Set(),
      remote: !!opts.remote,
    };
    try {
      for (const op of tx.ops) this.applyOne(op, inverse, ev);
    } catch (e) {
      // roll back what was applied so far
      for (let i = inverse.length - 1; i >= 0; i--) {
        try {
          this.applyOne(inverse[i], [], ev);
        } catch {
          /* ignore */
        }
      }
      throw e;
    }
    inverse.reverse();
    this.version++;
    if (!opts.silent) this.emit(ev);
    return inverse;
  }

  private batchDepth = 0;
  private pendingEvent: ChangeEvent | null = null;

  /** Merge all change events raised inside fn into one. */
  batch<T>(fn: () => T): T {
    this.batchDepth++;
    try {
      return fn();
    } finally {
      this.batchDepth--;
      if (this.batchDepth === 0 && this.pendingEvent) {
        const ev = this.pendingEvent;
        this.pendingEvent = null;
        this.notify(ev);
      }
    }
  }

  private emit(ev: ChangeEvent) {
    if (this.batchDepth > 0) {
      const p = this.pendingEvent;
      if (!p) {
        this.pendingEvent = { ...ev, tx: { ...ev.tx, ops: [...ev.tx.ops] } };
        return;
      }
      p.tx.ops.push(...ev.tx.ops);
      if (ev.tx.label && !p.tx.label) p.tx.label = ev.tx.label;
      for (const id of ev.created) p.created.add(id);
      for (const id of ev.deleted) {
        p.deleted.add(id);
        p.changed.delete(id);
        p.created.delete(id);
      }
      for (const id of ev.changed) p.changed.add(id);
      for (const k of ev.keys) p.keys.add(k);
      p.structural ||= ev.structural;
      return;
    }
    this.notify(ev);
  }

  /** The change is already applied: a listener that fails is reported, and the others still hear of it. */
  private notify(ev: ChangeEvent) {
    for (const l of this.listeners) {
      try {
        l(ev);
      } catch (e) {
        console.error('[vellum] a document listener failed:', e);
      }
    }
  }

  on(listener: ChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private applyOne(op: Op, inverse: Op[], ev: ChangeEvent) {
    switch (op.op) {
      case 'create': {
        const node = structuredClone(op.node);
        if (!node.id) throw new DocumentError('create: node.id required');
        if (this.nodes.has(node.id)) throw new DocumentError(`create: id ${node.id} already exists`);
        const parent = this.get(node.parent);
        if (!parent) throw new DocumentError(`create: parent ${node.parent} not found`);
        if (!isContainer(parent.type)) throw new DocumentError(`create: ${parent.type} cannot have children`);
        checkParent(node.type, parent.type, 'create');
        this.insert(node);
        inverse.push({ op: 'delete', id: node.id });
        ev.created.add(node.id);
        ev.changed.add(node.id);
        ev.structural = true;
        this.worldCache.clear();
        return;
      }
      case 'delete': {
        const node = this.nodes.get(op.id);
        if (!node) throw new DocumentError(`delete: ${op.id} not found`);
        if (node.type === 'DOCUMENT') throw new DocumentError('delete: cannot delete the document');
        // Delete subtree bottom-up; inverse recreates top-down.
        const sub = this.subtree(op.id);
        for (let i = sub.length - 1; i >= 0; i--) {
          const n = sub[i];
          this.remove(n.id);
          ev.deleted.add(n.id);
          ev.changed.delete(n.id);
          inverse.push({ op: 'create', node: n });
        }
        ev.structural = true;
        this.worldCache.clear();
        return;
      }
      case 'set': {
        const node = this.nodes.get(op.id);
        if (!node) throw new DocumentError(`set: ${op.id} not found`);
        const props = op.props as Record<string, unknown>;
        if ('id' in props) throw new DocumentError('set: cannot change id');
        if ('type' in props && props.type !== node.type && !(isFrameLike(node.type) && isFrameLike(props.type as NodeData['type'])))
          throw new DocumentError('set: cannot change type');
        const prev: Record<string, unknown> = {};
        const newParent = props.parent as NodeId | undefined;
        if (newParent !== undefined && newParent !== node.parent) {
          const p = this.get(newParent);
          if (!p) throw new DocumentError(`set: parent ${newParent} not found`);
          if (!isContainer(p.type)) throw new DocumentError(`set: ${p.type} cannot have children`);
          if (newParent === node.id || this.isAncestor(node.id, newParent)) throw new DocumentError('set: would create a cycle');
          checkParent(node.type, p.type, 'set');
        }
        const structural = 'parent' in props || 'index' in props;
        if (structural) this.unlink(node);
        for (const k of Object.keys(props)) {
          prev[k] = (node as unknown as Record<string, unknown>)[k];
          const v = props[k];
          if (v === undefined) delete (node as unknown as Record<string, unknown>)[k];
          else (node as unknown as Record<string, unknown>)[k] = structuredClone(v);
          ev.keys.add(k);
          if (GEOMETRY_KEYS.has(k)) this.worldCache.clear();
        }
        if (structural) {
          this.link(node);
          ev.structural = true;
        }
        inverse.push({ op: 'set', id: op.id, props: prev as NodeProps });
        ev.changed.add(op.id);
        return;
      }
      default:
        throw new DocumentError(`Unknown op ${(op as { op: string }).op}`);
    }
  }

  private insert(node: NodeData) {
    this.nodes.set(node.id, node);
    if (node.type === 'CONNECTOR') this.connectorIds.add(node.id);
    this.link(node);
  }

  private remove(id: NodeId) {
    const n = this.nodes.get(id);
    if (!n) return;
    this.unlink(n);
    this.nodes.delete(id);
    this.connectorIds.delete(id);
    this.childIds.delete(id);
    this.childCache.delete(id);
  }

  private link(node: NodeData) {
    if (!node.parent) return;
    let set = this.childIds.get(node.parent);
    if (!set) this.childIds.set(node.parent, (set = new Set()));
    set.add(node.id);
    this.childCache.delete(node.parent);
  }

  private unlink(node: NodeData) {
    if (!node.parent) return;
    this.childIds.get(node.parent)?.delete(node.id);
    this.childCache.delete(node.parent);
  }

  // -------------------------------------------------------------------------
  // Serialization
  // -------------------------------------------------------------------------

  toJSON(): DocumentJSON {
    return { format: 'vellum', version: 1, nodes: this.subtree(this.rootId) };
  }

  static fromJSON(json: DocumentJSON): Document {
    if (json.format !== 'vellum') throw new DocumentError('Not a vellum document');
    const root = json.nodes.find((n) => n.type === 'DOCUMENT');
    const doc = new Document(root?.id ?? '0:0');
    if (root) Object.assign(doc.must(doc.rootId), structuredClone({ ...root, parent: null }));
    const rest = json.nodes.filter((n) => n.type !== 'DOCUMENT');
    // Insert in an order where parents precede children.
    const byId = new Map(rest.map((n) => [n.id, n]));
    const done = new Set<string>([doc.rootId]);
    const ordered: NodeData[] = [];
    const visit = (n: NodeData) => {
      if (done.has(n.id)) return;
      const p = n.parent ? byId.get(n.parent) : undefined;
      if (p) visit(p);
      done.add(n.id);
      ordered.push(n);
    };
    rest.forEach(visit);
    // a layer missing fields every layer has (saved by an old or broken client) gets the editor's
    // defaults for them: renderers rely on them, and one bad layer would stop the whole file drawing
    const complete = (n: NodeData) => (BASE_KEYS.every((k) => k in n) ? n : makeNode(n.type, n));
    doc.apply({ ops: ordered.map((node) => ({ op: 'create', node: complete(node) })) }, { silent: true });
    return doc;
  }
}

const BASE_KEYS = ['name', 'x', 'y', 'width', 'height', 'rotation', 'visible', 'locked', 'opacity', 'fills', 'strokes', 'strokeWeight', 'effects'] as const;

const ROOT_LEVEL = new Set(['PAGE', 'STYLE', 'VARIABLE_COLLECTION']);

function checkParent(type: NodeData['type'], parentType: NodeData['type'], op: string) {
  if (ROOT_LEVEL.has(type) !== (parentType === 'DOCUMENT')) throw new DocumentError(`${op}: ${type} cannot be a child of ${parentType}`);
  if ((type === 'VARIABLE') !== (parentType === 'VARIABLE_COLLECTION')) throw new DocumentError(`${op}: ${type} cannot be a child of ${parentType}`);
}

function cmpIndex(a: NodeData, b: NodeData): number {
  return a.index < b.index ? -1 : a.index > b.index ? 1 : a.id < b.id ? -1 : 1;
}
